import { useState } from "react";
import { Instagram, MapPin, MessageCircle, Phone, UserRound } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "../components/ui/sonner";
import { contact } from "../data/siteData";
import { useSEO } from "../hooks/useSEO";

const initialForm = { name: "", email: "", phone: "", interest: "Surf Lessons", message: "" };

const openWhatsappMessage = (message) => {
  const url = `${contact.managerWhatsapp}?text=${encodeURIComponent(message)}`;
  window.open(url, "_blank", "noopener,noreferrer");
};

export default function Contact() {
  useSEO({
    title: "Contact — Plan Your Session",
    description: "Reach Kannur Surf Club via WhatsApp, direct phone, or Instagram. Send an inquiry to plan surf lessons, kite sessions, stays, or community events.",
    path: "/contact",
  });

  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);

  const updateField = (field, value) => setForm((current) => ({ ...current, [field]: value }));

  const submitInquiry = (event) => {
    event.preventDefault();
    setSubmitting(true);
    openWhatsappMessage(
      [
        "Hi Club Manager, I want to send an inquiry from kannursurfclub.in.",
        `Name: ${form.name}`,
        `Email: ${form.email}`,
        `Phone/WhatsApp: ${form.phone || "Not provided"}`,
        `Interest: ${form.interest}`,
        `Message: ${form.message}`,
      ].join("\n"),
    );
    toast.success("Opening WhatsApp with your inquiry details.");
    setForm(initialForm);
    setSubmitting(false);
  };

  return (
    <main data-testid="contact-page" className="bg-[#0A0F1C] px-5 pb-24 pt-32 text-white md:px-10 md:pb-32">
      <section data-testid="contact-layout-section" className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-[0.9fr_1.1fr]">
        <div>
          <p data-testid="contact-hero-kicker" className="text-xs font-bold uppercase tracking-[0.3em] text-[#FF5E3A]">Contact Us</p>
          <h1 data-testid="contact-hero-title" className="mt-6 font-heading text-5xl font-black leading-none tracking-tighter sm:text-6xl">Let’s connect before the next session.</h1>
          <p data-testid="contact-hero-description" className="mt-7 max-w-xl text-base leading-8 text-slate-400 md:text-lg">
            For surf lessons and quick coordination, contact the Club Manager by call or WhatsApp. For on-ground activity operations, contact the Activity Operator by call or WhatsApp. For daily updates, follow us on Instagram.
          </p>

          <div data-testid="contact-methods" className="mt-10 grid gap-4">
            <div data-testid="contact-manager-card" className="rounded-3xl border border-white/10 bg-[#1C1917]/50 p-5 backdrop-blur-xl">
              <p className="font-semibold text-white">Club Manager: {contact.managerPhone}</p>
              <div className="mt-3 flex flex-wrap gap-3">
                <a data-testid="contact-manager-phone-link" href={`tel:${contact.managerPhoneRaw}`} className="inline-flex items-center gap-2 rounded-xl border border-white/10 px-4 py-2 text-sm transition-colors duration-300 hover:border-white/30 hover:text-white">
                  <Phone className="h-4 w-4 text-[#FFC059]" /> Call
                </a>
                <a data-testid="contact-manager-whatsapp-link" href={contact.managerWhatsapp} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-xl border border-white/10 px-4 py-2 text-sm transition-colors duration-300 hover:border-white/30 hover:text-white">
                  <MessageCircle className="h-4 w-4 text-[#0D9488]" /> WhatsApp
                </a>
              </div>
            </div>
            <div data-testid="contact-owner-card" className="rounded-3xl border border-white/10 bg-[#1C1917]/50 p-5 backdrop-blur-xl">
              <p className="font-semibold text-white">Activity Operator: {contact.ownerPhone}</p>
              <div className="mt-3 flex flex-wrap gap-3">
                <a data-testid="contact-owner-phone-link" href={`tel:${contact.ownerPhoneRaw}`} className="inline-flex items-center gap-2 rounded-xl border border-white/10 px-4 py-2 text-sm transition-colors duration-300 hover:border-white/30 hover:text-white">
                  <Phone className="h-4 w-4 text-[#FFC059]" /> Call
                </a>
                <a data-testid="contact-owner-whatsapp-link" href={contact.ownerWhatsapp} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-xl border border-white/10 px-4 py-2 text-sm transition-colors duration-300 hover:border-white/30 hover:text-white">
                  <MessageCircle className="h-4 w-4 text-[#0D9488]" /> WhatsApp
                </a>
              </div>
            </div>
            <a data-testid="contact-instagram-link" href={contact.instagramUrl} target="_blank" rel="noreferrer" className="flex items-center gap-4 rounded-3xl border border-white/10 bg-[#1C1917]/50 p-5 backdrop-blur-xl transition-all duration-300 hover:border-white/20 hover:text-white">
              <Instagram className="h-5 w-5 text-[#FF5E3A]" /><span>{contact.instagramHandle}</span>
            </a>
          </div>

          <a data-testid="contact-big-instagram-button" href={contact.instagramUrl} target="_blank" rel="noreferrer" className="mt-8 flex items-center justify-between gap-4 rounded-3xl bg-[linear-gradient(135deg,#FF5E3A,#D946EF)] p-6 font-heading text-2xl font-black text-white transition-transform duration-300 hover:-translate-y-1">
            Follow Kannur Surf Club on Instagram
            <Instagram className="h-7 w-7" />
          </a>

          <div data-testid="contact-map-card" className="mt-8 overflow-hidden rounded-3xl border border-white/10 bg-[#1C1917]/50 backdrop-blur-xl">
            <div className="flex items-center gap-3 border-b border-white/10 p-5 text-sm text-slate-300"><MapPin className="h-4 w-4 text-[#FF5E3A]" />{contact.location}</div>
            <iframe
              data-testid="contact-map-iframe"
              title="Kannur Kerala map"
              src="https://www.google.com/maps?q=Kannur%20Kerala%20India&output=embed"
              className="h-72 w-full border-0 grayscale"
              loading="lazy"
            />
          </div>
        </div>

        <form data-testid="contact-form" onSubmit={submitInquiry} className="h-fit rounded-[2rem] border border-white/10 bg-[#1C1917]/50 p-7 backdrop-blur-xl md:p-10">
          <p data-testid="contact-form-kicker" className="text-xs font-bold uppercase tracking-[0.28em] text-[#FFC059]">Send an inquiry</p>
          <h2 data-testid="contact-form-title" className="mt-4 font-heading text-3xl font-bold text-white">Tell us what you’re planning.</h2>
          <div className="mt-8 grid gap-6">
            <label data-testid="contact-name-label" className="grid gap-2 text-sm text-slate-300">
              Name
              <Input data-testid="contact-name-input" required minLength={2} value={form.name} onChange={(event) => updateField("name", event.target.value)} placeholder="Your name" className="rounded-none border-0 border-b border-white/15 bg-transparent px-0 text-white placeholder:text-slate-600 focus-visible:ring-0" />
            </label>
            <label data-testid="contact-email-label" className="grid gap-2 text-sm text-slate-300">
              Email
              <Input data-testid="contact-email-input" required type="email" value={form.email} onChange={(event) => updateField("email", event.target.value)} placeholder="you@example.com" className="rounded-none border-0 border-b border-white/15 bg-transparent px-0 text-white placeholder:text-slate-600 focus-visible:ring-0" />
            </label>
            <label data-testid="contact-phone-label" className="grid gap-2 text-sm text-slate-300">
              Phone / WhatsApp
              <Input data-testid="contact-phone-input" value={form.phone} onChange={(event) => updateField("phone", event.target.value)} placeholder="Optional" className="rounded-none border-0 border-b border-white/15 bg-transparent px-0 text-white placeholder:text-slate-600 focus-visible:ring-0" />
            </label>
            <label data-testid="contact-interest-label" className="grid gap-2 text-sm text-slate-300">
              Interest
              <select data-testid="contact-interest-select" value={form.interest} onChange={(event) => updateField("interest", event.target.value)} className="h-11 border-0 border-b border-white/15 bg-[#131B2F] px-0 text-white outline-none transition-colors duration-300 focus:border-[#FF5E3A]">
                <option>Surf Lessons</option>
                <option>Kite Surfing</option>
                <option>Kayaking / Water Activities</option>
                <option>SUP Rentals</option>
                <option>Beach Events / Music / Wave Sessions</option>
                <option>Luxury High-Rise Apartment</option>
                <option>Eva’s Homestay</option>
                <option>Surf Camp Hostel Interest</option>
                <option>Custom Group Plan</option>
              </select>
            </label>
            <label data-testid="contact-message-label" className="grid gap-2 text-sm text-slate-300">
              Message
              <Textarea data-testid="contact-message-textarea" required minLength={8} value={form.message} onChange={(event) => updateField("message", event.target.value)} placeholder="Dates, group size, surf level, or stay preference" className="min-h-32 rounded-none border-0 border-b border-white/15 bg-transparent px-0 text-white placeholder:text-slate-600 focus-visible:ring-0" />
            </label>
            <Button data-testid="contact-form-submit-button" type="submit" disabled={submitting} className="h-12 rounded-xl bg-[#FF5E3A] font-semibold uppercase tracking-[0.16em] text-white hover:bg-[#FF7B5C] disabled:opacity-60">
              {submitting ? "Sending..." : "Send Inquiry"}
            </Button>
            <div data-testid="contact-form-routing-note" className="flex items-start gap-3 border border-white/10 bg-[#0A0F1C]/60 p-4 text-sm leading-7 text-slate-300">
              <UserRound className="mt-1 h-4 w-4 text-[#FFC059]" />
              You can call or WhatsApp both the Club Manager and Activity Operator. Inquiry submissions also open a WhatsApp draft to the Club Manager.
            </div>
          </div>
        </form>
      </section>
    </main>
  );
}
